import { useState, useRef } from "react";
import type { CreateAprDocumentPayload } from "@/lib/api";
import { CategoriaDoc, categoriasConfig } from "@/pages/documentacion/types";

interface Props {
  onClose: () => void;
  onConfirm: (payload: CreateAprDocumentPayload) => Promise<void>;
}

type Step = "form" | "processing" | "success";

export default function SubirDocumentoModal({ onClose, onConfirm }: Props) {
  const [step, setStep] = useState<Step>("form");
  const [nombre, setNombre] = useState("");
  const [categoria, setCategoria] = useState<CategoriaDoc>("legal");
  const [descripcion, setDescripcion] = useState("");
  const [version, setVersion] = useState("v1.0");
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [etiquetas, setEtiquetas] = useState("");
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileSizeLabel, setFileSizeLabel] = useState("0 KB");
  const [mimeType, setMimeType] = useState("application/octet-stream");
  const [fileExtension, setFileExtension] = useState<"pdf" | "xlsx" | "docx" | "jpg" | "zip">("pdf");

  const validate = () => {
    const e: Record<string, string> = {};
    if (!nombre.trim()) e.nombre = "Ingrese el nombre del documento";
    if (!descripcion.trim()) e.descripcion = "Ingrese una descripción";
    if (!fileName) e.archivo = "Seleccione un archivo";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setStep("processing");
    setRequestError("");
    try {
      await onConfirm({
        name: nombre.trim(),
        category: categoria,
        description: descripcion.trim(),
        expiresAt: fechaVencimiento || undefined,
        sizeLabel: fileSizeLabel,
        mimeType,
        fileExtension,
        uploadedBy: "Administración APR",
        version: version.trim() || "v1.0",
        tags: etiquetas
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
      });
      setStep("success");
    } catch (error) {
      setStep("form");
      setRequestError(error instanceof Error ? error.message : "No se pudo subir el documento");
    }
  };

  const categorias = Object.entries(categoriasConfig) as [CategoriaDoc, typeof categoriasConfig[CategoriaDoc]][];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50">
              <i className="ri-upload-cloud-line text-amber-600 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Subir Documento</h2>
              <p className="text-xs text-gray-400">Agregar nuevo archivo al repositorio APR</p>
            </div>
          </div>
          {step === "form" && (
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>

        {step === "processing" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 py-16">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-50">
              <i className="ri-loader-4-line text-2xl text-amber-600 animate-spin" />
            </div>
            <p className="text-sm text-gray-500">Subiendo documento...</p>
          </div>
        )}

        {step === "success" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 py-10 px-8">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100">
              <i className="ri-check-line text-3xl text-emerald-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-800">Documento subido</h3>
              <p className="text-sm text-gray-500 mt-1">El archivo ha sido agregado al repositorio correctamente</p>
            </div>
            <div className="w-full bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Nombre</span><span className="font-medium text-gray-800">{nombre}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Categoría</span><span className="font-medium text-gray-800">{categoriasConfig[categoria].label}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Versión</span><span className="font-medium text-gray-800">{version}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Archivo</span><span className="font-medium text-gray-800 truncate max-w-[60%]">{fileName}</span></div>
            </div>
            <button onClick={onClose} className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
              Cerrar
            </button>
          </div>
        )}

        {step === "form" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Archivo */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Archivo <span className="text-red-500">*</span></label>
                <div
                  onClick={() => fileRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    errors.archivo ? "border-red-300 bg-red-50/30" : fileName ? "border-teal-300 bg-teal-50/30" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.xlsx,.docx,.jpg,.zip"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setFileName(f.name);
                        setMimeType(f.type || "application/octet-stream");
                        setFileSizeLabel(formatFileSize(f.size));
                        setFileExtension(resolveExtension(f.name, f.type));
                        if (!nombre) setNombre(f.name.replace(/\.[^/.]+$/, ""));
                        setErrors((p) => ({ ...p, archivo: "" }));
                      }
                    }}
                  />
                  {fileName ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-100">
                        <i className="ri-file-check-line text-teal-600" />
                      </div>
                      <span className="text-sm font-medium text-teal-700">{fileName}</span>
                    </div>
                  ) : (
                    <>
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 mx-auto mb-2">
                        <i className="ri-upload-cloud-2-line text-xl text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">Haz click para seleccionar un archivo</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, Excel, Word, JPG, ZIP — máx. 20 MB</p>
                    </>
                  )}
                </div>
                {errors.archivo && <p className="text-xs text-red-500 mt-1">{errors.archivo}</p>}
              </div>

              {/* Nombre */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del documento <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Ej: Acta Asamblea Ordinaria Abril 2026"
                  value={nombre}
                  onChange={(e) => { setNombre(e.target.value); setErrors((p) => ({ ...p, nombre: "" })); }}
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 ${errors.nombre ? "border-red-400" : "border-gray-200"}`}
                />
                {errors.nombre && <p className="text-xs text-red-500 mt-1">{errors.nombre}</p>}
              </div>

              {/* Categoría */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Categoría</label>
                <div className="grid grid-cols-3 gap-2">
                  {categorias.map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => setCategoria(key)}
                      className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        categoria === key ? `border-current ${cfg.bg}` : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className={`w-6 h-6 flex items-center justify-center rounded-lg ${categoria === key ? cfg.bg : "bg-gray-100"} flex-shrink-0`}>
                        <i className={`${cfg.icon} text-xs ${categoria === key ? cfg.color : "text-gray-400"}`} />
                      </div>
                      <span className={`text-xs font-medium leading-tight ${categoria === key ? cfg.color : "text-gray-500"}`}>{cfg.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Descripción <span className="text-red-500">*</span></label>
                <textarea
                  rows={2}
                  maxLength={300}
                  placeholder="Breve descripción del contenido del documento..."
                  value={descripcion}
                  onChange={(e) => { setDescripcion(e.target.value); setErrors((p) => ({ ...p, descripcion: "" })); }}
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 resize-none ${errors.descripcion ? "border-red-400" : "border-gray-200"}`}
                />
                {errors.descripcion && <p className="text-xs text-red-500 mt-1">{errors.descripcion}</p>}
              </div>

              {/* Versión y vencimiento */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Versión</label>
                  <input
                    type="text"
                    placeholder="v1.0"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de vencimiento <span className="text-gray-400 font-normal">(opcional)</span></label>
                  <input
                    type="date"
                    value={fechaVencimiento}
                    onChange={(e) => setFechaVencimiento(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400"
                  />
                </div>
              </div>

              {/* Etiquetas */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Etiquetas <span className="text-gray-400 font-normal">(separadas por coma)</span></label>
                <input
                  type="text"
                  placeholder="Ej: acta, 2026, asamblea"
                  value={etiquetas}
                  onChange={(e) => setEtiquetas(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400"
                />
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              {requestError && (
                <p className="text-xs text-red-500 mr-2">{requestError}</p>
              )}
              <button
                onClick={() => {
                  void handleSubmit();
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-cloud-line" /></div>
                Subir Documento
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function resolveExtension(
  fileName: string,
  mimeType: string,
): "pdf" | "xlsx" | "docx" | "jpg" | "zip" {
  const lowerName = fileName.toLowerCase();
  if (lowerName.endsWith(".pdf")) return "pdf";
  if (lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls")) return "xlsx";
  if (lowerName.endsWith(".docx") || lowerName.endsWith(".doc")) return "docx";
  if (lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg") || lowerName.endsWith(".png")) return "jpg";
  if (lowerName.endsWith(".zip")) return "zip";

  const lowerMime = mimeType.toLowerCase();
  if (lowerMime.includes("pdf")) return "pdf";
  if (lowerMime.includes("sheet") || lowerMime.includes("excel")) return "xlsx";
  if (lowerMime.includes("word")) return "docx";
  if (lowerMime.includes("jpeg") || lowerMime.includes("jpg") || lowerMime.includes("png")) return "jpg";
  if (lowerMime.includes("zip")) return "zip";
  return "pdf";
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
